// WHAT: Enhanced roadmap item card component with progress indicators and ROI calculation
// WHY:  Provides a comprehensive visual representation of roadmap items with impact/effort analysis
// DOMAIN: Admin → Roadmap → Overview
// DEPENDENCIES: Roadmap_Item types, roadmapMapping utilities, Lucide icons
// RISKS:
//   - NOTE: ROI calculation is simplified (impact > effort = high ROI)
//   - NOTE: Progress indicator uses CSS variables - ensure theme is properly configured
//   - NOTE: Component is memoized - ensure props are stable to prevent unnecessary re-renders

import {
  LucideBug,
  LucideLightbulb,
  LucideSparkles,
  LucideTarget,
  LucideTrash2,
  LucideEdit3,
  LucideClock,
  LucideCheckCircle,
  LucideXCircle,
  LucideAlertCircle,
  LucideTrendingUp,
  LucideZap,
} from "lucide-react";
import React, { memo, useMemo } from "react";

import {
  type Roadmap_Item,
  type Roadmap_Item_Type,
  type Roadmap_Item_Score,
} from "@/domains/admin/roadmap/types/roadmap-types";
import { typeToString, scoreLevelToString } from "@/domains/admin/roadmap/utils/roadmapMapping";

/* ------------------------------------------------------------ */
/* Types                                                         */
/* ------------------------------------------------------------ */

/**
 * Props for RoadmapItemCard component
 */
export interface RoadmapItemCardProperties {
  /** Roadmap item to display */
  item: Roadmap_Item;
  /** Callback when delete button is clicked */
  onDelete: (item: Roadmap_Item) => void;
  /** Callback when edit button is clicked */
  onEdit: (item: Roadmap_Item) => void;
}

/* ------------------------------------------------------------ */
/* Branding-Compliant Icon Mapping                               */
/* ------------------------------------------------------------ */

const iconByType: Record<Roadmap_Item_Type, React.ReactElement> = {
  bug: <LucideBug className="w-5 h-5 text-[var(--color-error-500)]" />,
  feature: <LucideTarget className="w-5 h-5 text-[var(--color-primary-500)]" />,
  idea: <LucideLightbulb className="w-5 h-5 text-[var(--color-tertiary-500)]" />,
  insight: <LucideSparkles className="w-5 h-5 text-[var(--color-success-500)]" />,
};

const getTypeIcon = (type: Roadmap_Item_Type) => iconByType[type];

/* ------------------------------------------------------------ */
/* Progress Indicator Component                                  */
/* ------------------------------------------------------------ */

/**
 * Calculate score value from string representation
 *
 * @param score - Score string value
 *
 * @returns {number} Numeric score value
 *
 */
const getScoreValue = (score?: Roadmap_Item_Score): number => {
  switch (score) {
    case "low": {
      return 1;
    }
    case "medium": {
      return 2;
    }
    case "high": {
      return 3;
    }
    default: {
      return 0;
    }
  }
};

/**
 * Calculate ROI label based on impact and effort values
 *
 * @param impactValue - Impact numeric value
 *
 * @param effortValue - Effort numeric value
 *
 * @returns {string} ROI label
 *
 */
const getROILabel = (impactValue: number, effortValue: number): string => {
  if (impactValue > effortValue) {
    return "High ROI";
  }
  if (impactValue < effortValue) {
    return "Low ROI";
  }
  return "Balanced";
};

/**
 * Calculate ROI color class based on impact and effort values
 *
 * @param impactValue - Impact numeric value
 *
 * @param effortValue - Effort numeric value
 *
 * @returns {string} ROI color class
 *
 */
const getROIColorClass = (impactValue: number, effortValue: number): string => {
  if (impactValue > effortValue) {
    return "text-[var(--color-success-500)]";
  }
  if (impactValue < effortValue) {
    return "text-[var(--color-warning-500)]";
  }
  return "text-[var(--color-neutral-400)]";
};

const ProgressIndicator: React.FC<{
  impact?: Roadmap_Item_Score;
  effort?: Roadmap_Item_Score;
}> = memo(({ effort, impact }) => {
  const { effortValue, impactValue } = useMemo(() => {
    const impactVal = getScoreValue(impact);
    const effortVal = getScoreValue(effort);

    return { effortValue: effortVal, impactValue: impactVal };
  }, [impact, effort]);

  if (!impact && !effort) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs">
        <span className="text-[var(--color-neutral-400)] font-medium">Impact vs Effort</span>
        <div className="flex items-center gap-1">
          <LucideTrendingUp className="w-3 h-3 text-[var(--color-primary-400)]" />
          <span className="text-[var(--color-neutral-300)]">ROI</span>
        </div>
      </div>

      <div className="relative h-2 bg-[var(--color-neutral-700)] rounded-full overflow-hidden">
        {/* Impact Bar */}
        {impact ? (
          <div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] rounded-full transition-all duration-500"
            style={{ width: `${String((impactValue / 3) * 100)}%` }}
          />
        ) : null}

        {/* Effort Bar (overlay) */}
        {effort ? (
          <div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-[var(--color-accent-500)] to-[var(--color-accent-400)] rounded-full opacity-60 transition-all duration-500"
            style={{ width: `${String((effortValue / 3) * 100)}%` }}
          />
        ) : null}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          {impact ? (
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-[var(--color-primary-500)] rounded-full" />
              <span className="text-[var(--color-neutral-400)]">Impact</span>
            </div>
          ) : null}
          {effort ? (
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-[var(--color-accent-500)] rounded-full" />
              <span className="text-[var(--color-neutral-400)]">Effort</span>
            </div>
          ) : null}
        </div>

        {/* ROI Indicator */}
        {impact && effort ? (
          <div className="flex items-center gap-1">
            <LucideZap className="w-3 h-3 text-[var(--color-tertiary-500)]" />
            <span className={`text-xs font-medium ${getROIColorClass(impactValue, effortValue)}`}>
              {getROILabel(impactValue, effortValue)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
});

ProgressIndicator.displayName = "ProgressIndicator";

/* ------------------------------------------------------------ */
/* Enhanced Status Badge Component                               */
/* ------------------------------------------------------------ */

const StatusBadge: React.FC<{ status: string }> = memo(({ status }) => {
  const statusConfig = useMemo(
    () => ({
      done: {
        bgColor: "bg-[var(--color-success-500)] bg-opacity-10",
        borderColor: "border-[var(--color-success-500)] border-opacity-20",
        icon: <LucideCheckCircle className="w-3.5 h-3.5" />,
        textColor: "text-[var(--color-success-500)]",
      },
      in_progress: {
        bgColor: "bg-[var(--color-warning-500)] bg-opacity-10",
        borderColor: "border-[var(--color-warning-500)] border-opacity-20",
        icon: <LucideAlertCircle className="w-3.5 h-3.5" />,
        textColor: "text-[var(--color-warning-500)]",
      },
      open: {
        bgColor: "bg-[var(--color-info-500)] bg-opacity-10",
        borderColor: "border-[var(--color-info-500)] border-opacity-20",
        icon: <LucideClock className="w-3.5 h-3.5" />,
        textColor: "text-[var(--color-info-500)]",
      },
      rejected: {
        bgColor: "bg-[var(--color-error-500)] bg-opacity-10",
        borderColor: "border-[var(--color-error-500)] border-opacity-20",
        icon: <LucideXCircle className="w-3.5 h-3.5" />,
        textColor: "text-[var(--color-error-500)]",
      },
    }),
    [],
  );

  const statusKey = status as keyof typeof statusConfig;
  const config = statusKey in statusConfig ? statusConfig[statusKey] : statusConfig.open;

  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 hover:scale-105 ${config.bgColor} ${config.textColor} ${config.borderColor}`}
    >
      {config.icon}
      {status.replace("_", " ")}
    </span>
  );
});

StatusBadge.displayName = "StatusBadge";

/* ------------------------------------------------------------ */
/* Enhanced Score Badge Component                                */
/* ------------------------------------------------------------ */

const ScoreBadge: React.FC<{
  label: string;
  score: Roadmap_Item_Score;
  color: string;
}> = memo(({ color, label, score }) => {
  const scoreIcon = useMemo(() => {
    const scoreValue = scoreLevelToString(score).toLowerCase();
    if (scoreValue.includes("high")) {
      return "🔥";
    }
    if (scoreValue.includes("medium")) {
      return "⚡";
    }
    if (scoreValue.includes("low")) {
      return "💡";
    }
    return "📊";
  }, [score]);

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--color-neutral-700)] ${color} transition-all duration-200 hover:bg-[var(--color-neutral-600)]`}
    >
      <span className="text-xs">{scoreIcon}</span>
      {label}: {scoreLevelToString(score)}
    </span>
  );
});

ScoreBadge.displayName = "ScoreBadge";

/* ------------------------------------------------------------ */
/* Main Component (Enhanced UX Design)                          */
/* ------------------------------------------------------------ */

const RoadmapItemCardBase: React.FC<RoadmapItemCardProperties> = ({ item, onDelete, onEdit }) => {
  const typeIcon = useMemo(() => getTypeIcon(item.type), [item.type]);
  const formattedDate = useMemo(
    () =>
      new Date(item.updated_at).toLocaleDateString("de-DE", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    [item.updated_at],
  );

  const handleEdit = useMemo(
    () => () => {
      onEdit(item);
    },
    [onEdit, item],
  );
  const handleDelete = useMemo(
    () => () => {
      onDelete(item);
    },
    [onDelete, item],
  );

  return (
    <div className="card-hover p-6 rounded-2xl transition-all duration-300 flex flex-col justify-between min-h-[320px] group hover:scale-[1.02] hover:shadow-2xl">
      {/* ---------- Header Section ---------- */}
      <div className="space-y-4">
        {/* Type & Module Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-1.5 rounded-lg bg-[var(--color-neutral-700)] group-hover:bg-[var(--color-neutral-600)] transition-colors duration-200">
              {typeIcon}
            </div>
            <span className="text-sm font-semibold uppercase tracking-wide text-[var(--color-neutral-300)]">
              {typeToString(item.type)}
            </span>
          </div>
          <span className="text-xs font-medium text-[var(--color-neutral-400)] bg-[var(--color-neutral-800)] px-2 py-1 rounded-md group-hover:bg-[var(--color-neutral-700)] transition-colors duration-200">
            {item.module}
          </span>
        </div>

        {/* Title with enhanced hover effect */}
        <h3 className="text-[var(--color-neutral-100)] font-semibold text-lg leading-tight group-hover:text-[var(--color-primary-400)] transition-colors duration-200 cursor-pointer">
          {item.title}
        </h3>

        {/* Description with improved readability */}
        <p className="text-sm text-[var(--color-neutral-400)] leading-relaxed line-clamp-3 group-hover:text-[var(--color-neutral-300)] transition-colors duration-200">
          {item.description}
        </p>

        {/* Status Badge with enhanced styling */}
        <div className="flex items-center gap-2">
          <StatusBadge status={item.status} />
          {/* ROI Indicator for top items */}
          {"roi" in item && typeof item.roi === "number" && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[var(--color-success-500)] bg-opacity-10 text-[var(--color-success-400)] border border-[var(--color-success-500)] border-opacity-20">
              <LucideTrendingUp className="w-3 h-3" />
              ROI: {item.roi.toFixed(1)}
            </span>
          )}
        </div>

        {/* Progress Indicator */}
        {(() => {
          const progressProps: { effort?: Roadmap_Item_Score; impact?: Roadmap_Item_Score } = {};
          if (item.effort_estimate) {
            progressProps.effort = item.effort_estimate;
          }
          if (item.impact_score) {
            progressProps.impact = item.impact_score;
          }
          return item.impact_score || item.effort_estimate ? (
            <ProgressIndicator {...progressProps} />
          ) : null;
        })()}

        {/* Score Badges Grid with improved layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {item.priority ? (
            <ScoreBadge
              color="text-[var(--color-accent-500)]"
              label="Priority"
              score={item.priority}
            />
          ) : null}
          {item.impact_score ? (
            <ScoreBadge
              color="text-[var(--color-primary-400)]"
              label="Impact"
              score={item.impact_score}
            />
          ) : null}
          {item.effort_estimate ? (
            <ScoreBadge
              color="text-[var(--color-success-500)]"
              label="Effort"
              score={item.effort_estimate}
            />
          ) : null}
        </div>
      </div>

      {/* ---------- Footer Section ---------- */}
      <div className="flex items-center justify-between pt-4 mt-auto border-t border-[var(--color-neutral-700)] group-hover:border-[var(--color-neutral-600)] transition-colors duration-200">
        {/* Date with enhanced formatting */}
        <span className="text-xs text-[var(--color-neutral-500)] font-medium group-hover:text-[var(--color-neutral-400)] transition-colors duration-200">
          {formattedDate}
        </span>

        {/* Action Buttons with enhanced interactions */}
        <div className="flex items-center gap-1">
          <button
            aria-label="Edit roadmap item"
            className="p-2 text-[var(--color-neutral-400)] hover:text-[var(--color-primary-400)] hover:bg-[var(--color-primary-500)] hover:bg-opacity-10 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-400)] focus:ring-offset-2 focus:ring-offset-[var(--color-neutral-800)] transform hover:scale-110"
            onClick={handleEdit}
            title="Edit this item"
          >
            <LucideEdit3 className="w-4 h-4" />
          </button>

          <button
            aria-label="Delete roadmap item"
            className="p-2 text-[var(--color-neutral-400)] hover:text-[var(--color-error-500)] hover:bg-[var(--color-error-500)] hover:bg-opacity-10 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--color-error-500)] focus:ring-offset-2 focus:ring-offset-[var(--color-neutral-800)] transform hover:scale-110"
            onClick={handleDelete}
            title="Delete this item"
          >
            <LucideTrash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------ */
/* Exports (TracQ-Standard)                                      */
/* ------------------------------------------------------------ */

export const RoadmapItemCard = memo(RoadmapItemCardBase);
